import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Loading, Empty, ErrorState } from '../components/ui';

type OverdueItem = {
  stepId: string;
  level: number;
  role: string;
  assignee: { id: string; name: string } | null;
  dueAt: string | null;
  daysLate: number;
  revCode: string;
  document: { id: string; docNumber: string; title: string };
};

/**
 * "Tahu persis gambar mana yang menahan pekerjaan di lapangan" —
 * the promise made for this module in the design document. It can
 * only be kept if every approval step carries an SLA, which is why
 * dueAt sits on ApprovalStep rather than on the document.
 *
 * Ordered by how late, not by discipline or number. The field does
 * not care whose drawing it is.
 */
export function Overdue() {
  const navigate = useNavigate();
  const [items, setItems] = useState<OverdueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const res = await api.get<{ items: OverdueItem[] }>('/inbox/overdue');
      setItems(res.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load overdue work.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Blocking site work</h1>
          <p>
            {items === null
              ? 'Loading…'
              : items.length === 0
                ? 'Nothing is past its review date'
                : `${items.length} approval ${items.length === 1 ? 'step is' : 'steps are'} past due`}
          </p>
        </div>
      </div>

      {error && <ErrorState message={error} onRetry={load} />}
      {!error && items === null && <Loading />}

      {!error && items !== null && items.length === 0 && (
        <Empty
          title="Nothing is late"
          body="Every approval step is inside its review window."
          action={
            <button className="btn btn-ghost" onClick={() => navigate('/')}>
              Open the register
            </button>
          }
        />
      )}

      {!error && items !== null && items.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Number</th>
                <th>Title</th>
                <th>Rev</th>
                <th>Waiting on</th>
                <th>Late by</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr
                  key={i.stepId}
                  onClick={() => navigate(`/documents/${i.document.id}`)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/documents/${i.document.id}`)}
                >
                  <td className="num">{i.document.docNumber}</td>
                  <td>{i.document.title}</td>
                  <td className="num">{i.revCode}</td>
                  <td>
                    {i.assignee ? i.assignee.name : `Any ${i.role.toLowerCase()}`} · L{i.level}
                  </td>
                  <td>
                    {/* Label, never colour alone — red and green are
                        indistinguishable under deuteranopia. */}
                    <span className="chip crit">
                      {i.daysLate} {i.daysLate === 1 ? 'day' : 'days'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
